/**
 * Keep the previous value's identity wherever the new one is equal, deep.
 *
 * Every TV session update arrives as fresh objects (Firestore parses the
 * whole document), so without this a change to one HUD field (Football's
 * result popup, a heartbeat, a status line) handed the 3D stage a new
 * `scene` and re-rendered every mesh in it. On the Apple TV that render runs
 * on the same JS thread as the frame loop, and it landed in the middle of
 * the after-play camera move (Cole, 2026-10-17: "a tiny bit choppy"). With
 * the old identity kept, an unchanged scene is not rendered again, and a
 * changed one keeps its unchanged arrays (rolloutBands, extraCups).
 *
 * Plain JSON data only: objects, arrays and primitives.
 */
export function shareStructure<T>(prev: unknown, next: T): T {
  if (Object.is(prev, next)) return next;
  if (prev === null || next === null || typeof prev !== 'object' || typeof next !== 'object') return next;
  if (Array.isArray(prev) !== Array.isArray(next)) return next;
  if (Array.isArray(next)) {
    const before = prev as unknown[];
    let same = before.length === next.length;
    const out = next.map((item, index) => {
      const shared = shareStructure(before[index], item);
      if (shared !== before[index]) same = false;
      return shared;
    });
    return (same ? before : out) as T;
  }
  const before = prev as Record<string, unknown>;
  const after = next as Record<string, unknown>;
  const keys = Object.keys(after);
  let same = keys.length === Object.keys(before).length;
  const out: Record<string, unknown> = {};
  for (const key of keys) {
    const shared = shareStructure(before[key], after[key]);
    if (!Object.prototype.hasOwnProperty.call(before, key) || shared !== before[key]) same = false;
    out[key] = shared;
  }
  return (same ? before : out) as T;
}
